import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Play, ChevronDown, X, ExternalLink } from "lucide-react";
import { resolveMedia } from "../../lib/api";
import { useLenisReduced } from "../../lib/motionFlag";
import { parseYouTubeUrl, isDirectVideoUrl } from "../../lib/youtube";

const DEFAULT_HERO_BG = "https://static.prod-images.emergentagent.com/jobs/c6f51fbc-ba88-4cac-a357-6a477f785856/images/052fd9c777a93a24a010dc9244775e2bc0920accf6c6276bbdeb3496743a3235.jpeg";

const lineClass = (style) => {
  if (style === "orange") return "text-[#FF5722]";
  if (style === "outline") return "text-outline";
  return "text-white";
};

export default function Hero({ hero }) {
  const ref = useRef(null);
  const reduced = useLenisReduced();
  const [showreel, setShowreel] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", reduced ? "0%" : "18%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", reduced ? "0%" : "-8%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0.2]);

  const h = hero || {};
  const lines = h.headline_lines || [];
  const stats = h.stats || [];

  // Identify showreel media sources
  const showreelRawUrl = h.cta_showreel?.url || h.showreel_url || (!isDirectVideoUrl(h.video_url) ? h.video_url : "") || "";
  const yt = parseYouTubeUrl(showreelRawUrl);
  const directVideo = isDirectVideoUrl(showreelRawUrl)
    ? showreelRawUrl
    : isDirectVideoUrl(h.video_url)
    ? h.video_url
    : null;

  // Only render background <video> if h.video_url is an actual direct video file
  const hasDirectBgVideo = isDirectVideoUrl(h.video_url);

  // Close showreel modal on ESC key
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && showreel) setShowreel(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showreel]);

  return (
    <section ref={ref} id="hero" data-testid="hero-section" className="relative min-h-[100svh] w-full overflow-hidden bg-navy">
      {/* Background media */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 parallax-layer">
        {hasDirectBgVideo ? (
          <video
            className="h-full w-full object-cover"
            autoPlay muted loop playsInline
            preload="metadata"
            poster={resolveMedia(h.poster_image || h.bg_image || DEFAULT_HERO_BG)}
          >
            <source src={resolveMedia(h.video_url)} type="video/mp4" />
          </video>
        ) : (
          <img
            src={resolveMedia(h.bg_image || DEFAULT_HERO_BG)}
            alt="ADMALL production environment"
            fetchpriority="high"
            decoding="async"
            className="h-full w-full object-cover"
          />
        )}
      </motion.div>

      {/* Cinematic overlays — navy, no pink/neon */}
      <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy-dark via-transparent to-navy/40" />

      {/* Content */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 mx-auto flex min-h-[100svh] max-w-7xl flex-col justify-center px-5 pt-28 pb-28 sm:px-8 lg:pb-16"
      >
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
          data-testid="hero-eyebrow"
          className="mb-5 max-w-xl font-body text-[10px] font-semibold uppercase tracking-[0.2em] text-white/70 sm:text-xs"
        >
          {h.eyebrow}
        </motion.p>

        <h1 data-testid="hero-headline" className="font-display text-[2.8rem] font-bold uppercase leading-[0.98] tracking-tight sm:text-7xl lg:text-8xl xl:text-[8.75rem]">
          {lines.map((ln, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                className={`block ${lineClass(ln.style)}`}
                initial={{ y: "110%" }} animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.25 + i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              >
                {ln.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1 }}
          className="mt-6 max-w-lg"
        >
          <div className="mb-3 flex items-center gap-3">
            <span className="h-px w-8 bg-[#FF5722]" />
            <span data-testid="hero-subheading" className="font-display text-lg font-bold tracking-[0.15em] text-white sm:text-xl">
              {h.subheading}
            </span>
          </div>
          <p className="font-body text-sm leading-relaxed text-white/70 sm:text-base">{h.support_text}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.15 }}
          className="mt-8 flex flex-wrap items-center gap-3 sm:gap-4"
        >
          <a
            href={h.cta_primary?.url || "#contact"}
            data-testid="hero-cta-primary"
            className="inline-flex items-center gap-2 rounded-full bg-[#FF5722] px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white transition-all hover:bg-[#E64A19] sm:px-8"
          >
            {h.cta_primary?.label || "START A PROJECT"} <ArrowRight size={16} />
          </a>
          <a
            href={h.cta_secondary?.url || "#services"}
            data-testid="hero-cta-secondary"
            className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white transition-all hover:border-white hover:bg-white/10 sm:px-8"
          >
            {h.cta_secondary?.label || "EXPLORE SERVICES"} <ArrowRight size={16} />
          </a>
          {(h.cta_showreel?.label) && (
            <button data-testid="hero-showreel-btn" onClick={() => setShowreel(true)}
              className="group inline-flex items-center gap-3 font-display text-sm font-bold uppercase tracking-wider text-white">
              <span className="flex h-11 w-11 items-center justify-center rounded-full border border-white/40 transition-all group-hover:scale-110 group-hover:border-[#FF5722] group-hover:bg-[#FF5722]">
                <Play size={16} className="ml-0.5" fill="currentColor" />
              </span>
              {h.cta_showreel.label}
            </button>
          )}
        </motion.div>

        {/* Stats strip */}
        {stats.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.35 }}
            data-testid="hero-stats"
            className="mt-12 flex flex-wrap gap-x-10 gap-y-5 border-t border-white/10 pt-6"
          >
            {stats.map((st, i) => (
              <div key={i} className="flex flex-col">
                <span className="font-display text-3xl font-black text-[#FF5722] sm:text-4xl">{st.value}</span>
                <span className="font-body text-[10px] font-semibold uppercase tracking-wider text-white/60 sm:text-xs">{st.label}</span>
              </div>
            ))}
          </motion.div>
        )}
      </motion.div>

      {/* Scroll indicator */}
      <div className="absolute bottom-24 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 lg:flex">
        <span className="font-body text-[10px] font-semibold uppercase tracking-[0.3em] text-white/50">Scroll to Explore</span>
        <ChevronDown size={18} className="animate-bounce text-white/50" />
      </div>

      {/* Showreel modal */}
      {showreel && (
        <div
          data-testid="showreel-modal"
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90 p-4 sm:p-6"
          onClick={() => setShowreel(false)}
        >
          <button
            data-testid="showreel-close"
            className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white/80 transition hover:bg-white/20 hover:text-white"
            onClick={() => setShowreel(false)}
            aria-label="Close Showreel"
          >
            <X size={24} />
          </button>
          <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            {yt.isValid ? (
              <div className="flex flex-col">
                <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-black shadow-2xl border border-white/10">
                  <iframe
                    src={yt.embedUrl}
                    title="ADMALL INDIA Agency Showreel"
                    className="h-full w-full border-0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
                <div className="mt-3 flex items-center justify-between text-xs text-white/70">
                  <span className="font-display font-bold uppercase tracking-wider text-white">
                    ADMALL INDIA • Agency Showreel
                  </span>
                  <a
                    href={yt.watchUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 font-semibold text-[#FF5722] hover:underline"
                  >
                    Open directly on YouTube <ExternalLink size={12} />
                  </a>
                </div>
              </div>
            ) : directVideo ? (
              <div className="overflow-hidden rounded-2xl shadow-2xl bg-black border border-white/10">
                <video className="w-full rounded-2xl max-h-[80vh]" controls autoPlay playsInline>
                  <source src={resolveMedia(directVideo)} type="video/mp4" />
                </video>
              </div>
            ) : (
              <div className="flex aspect-video w-full flex-col items-center justify-center rounded-2xl border border-white/15 bg-navy p-8 text-center">
                <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#FF5722]/15 text-[#FF5722]">
                  <Play size={32} className="ml-0.5 fill-[#FF5722]" />
                </span>
                <p className="px-6 font-display text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">
                  Showreel Coming Soon
                </p>
                <p className="mt-2 max-w-md font-body text-sm leading-relaxed text-white/60">
                  Configure your YouTube showreel link or upload a commercial reel from the ADMALL CMS.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
